import { defineColumn, type StrictColumnDef } from '../../../../components/tanstack-table/column';
import { casesOf, unitsOf } from '../../../../data/order-item';
import type { ProductOrderItem } from '../data/product-order-item';
import { navigateNamedInputs } from '../utils/navigate-named-inputs';
import { CountCell } from './count-cell';

// Shared name across every input in a given column so Enter / Shift+Enter can
// walk them in DOM order (which matches the sorted visible order in the table).
function countInputName(field: 'cases' | 'units'): string {
  return `${field}-input`;
}

export function makeCountColumn(
  setOrderItem: (orderItem: ProductOrderItem) => void,
  field: 'cases' | 'units'
): StrictColumnDef<ProductOrderItem> {
  function getCount(orderItem: ProductOrderItem): number {
    return field === 'cases' ? casesOf(orderItem) : unitsOf(orderItem);
  }

  function setCount(orderItem: ProductOrderItem, count: number) {
    if (field === 'cases') {
      // When updating case quantities, we assume the user does not want to order units & reset them to 0
      setOrderItem({ ...orderItem, cases: count, units: 0 });
    } else {
      setOrderItem({ ...orderItem, cases: orderItem.cases, units: count });
    }
  }

  const id = field === 'cases' ? 'Order Cases' : 'Order Units';
  const inputName = countInputName(field);

  return defineColumn<ProductOrderItem>({
    id,
    minWidth: 80,
    accessorFn: (orderItem) => `${getCount(orderItem)}`,
    cell: ({ item: orderItem }) => (
      <CountCell
        orderItem={orderItem}
        getCount={getCount}
        setCount={setCount}
        name={inputName}
        onKeyDown={(event) => navigateNamedInputs(event, inputName)}
        onFocus={(event) => event.currentTarget.select()}
      />
    ),
  });
}
